import React, { useState, useEffect, useCallback } from 'react';
import { Tag, Plus, Pencil, Trash2, Star, Eye, EyeOff, CreditCard } from 'lucide-react';
import { adminAPI } from '../../services/api';
import Card from '../common/Card';
import Button from '../common/Button';
import Input from '../common/Input';
import Alert from '../common/Alert';
import Modal from '../common/Modal';
import LoadingSpinner from '../common/LoadingSpinner';

const UNLIMITED = -1;

// Blank plan used when creating a new tier.
const emptyPlan = () => ({
  key: '',
  label: '',
  tagline: '',
  paid: true,
  priceUsd: 0,
  priceInr: 0,
  maxLinks: 100,
  maxProfilePages: 5,
  maxLinksPerProfilePage: 25,
  maxWebhooks: 5,
  isHighlighted: false,
  isActive: true,
  displayOrder: 0,
  stripePriceMonth: '',
  stripePriceYear: '',
  razorpayPlanMonth: '',
  razorpayPlanYear: '',
});

const fmtQuota = (n) => (n === UNLIMITED ? 'Unlimited' : n);

const PlanManagement = () => {
  const [plans, setPlans] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editing, setEditing] = useState(null); // plan object being edited/created
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setError('');
    const res = await adminAPI.getPlans();
    if (res.success) setPlans(res.data.plans);
    else setError(res.message || res.error || 'Failed to load plans');
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(emptyPlan()); setIsNew(true); };
  const openEdit = (plan) => { setEditing({ ...plan }); setIsNew(false); };
  const closeEditor = () => { setEditing(null); setIsNew(false); };

  const setField = (field, value) => setEditing((p) => ({ ...p, [field]: value }));

  const save = async () => {
    setSaving(true);
    setError(''); setSuccess('');
    try {
      // Strip empty provider-id strings to null; coerce numerics.
      const payload = {
        ...editing,
        priceUsd: Number(editing.priceUsd) || 0,
        priceInr: Number(editing.priceInr) || 0,
        displayOrder: Number(editing.displayOrder) || 0,
        stripePriceMonth: editing.stripePriceMonth || null,
        stripePriceYear: editing.stripePriceYear || null,
        razorpayPlanMonth: editing.razorpayPlanMonth || null,
        razorpayPlanYear: editing.razorpayPlanYear || null,
      };
      const res = isNew
        ? await adminAPI.createPlan(payload)
        : await adminAPI.updatePlan(editing.key, payload);
      if (!res.success) {
        setError(res.message || res.error || 'Failed to save plan');
        return;
      }
      setSuccess(isNew ? `Plan "${editing.key}" created` : `Plan "${editing.key}" updated`);
      closeEditor();
      await load();
    } catch (e) {
      setError(e.message || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (plan) => {
    if (!window.confirm(`Delete the "${plan.label}" (${plan.key}) plan? This cannot be undone.`)) return;
    setError(''); setSuccess('');
    const res = await adminAPI.deletePlan(plan.key);
    if (res.success) { setSuccess(`Plan "${plan.key}" deleted`); load(); }
    else setError(res.message || res.error || 'Failed to delete plan');
  };

  if (!plans) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <LoadingSpinner />
        <p className="mt-4 text-sm text-ink-500">Loading plans…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border p-6 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center">
          <div className="flex items-center justify-center w-12 h-12 bg-brand-100 rounded-xl mr-4">
            <Tag className="h-7 w-7 text-brand-600" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-ink-900">Pricing Plans</h1>
            <p className="text-ink-600 mt-1">
              Edit prices, quotas and visibility. Changes drive both enforcement and the public pricing page.
            </p>
          </div>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4" /> Add plan
        </Button>
      </div>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {plans.map((plan) => (
          <Card key={plan.key} className="p-5 flex flex-col">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-ink-900 truncate">{plan.label}</h3>
                  {plan.isHighlighted && (
                    <span title="Popular badge" className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                      <Star className="h-3 w-3" /> Popular
                    </span>
                  )}
                </div>
                <p className="text-xs text-ink-400 font-mono">{plan.key}</p>
              </div>
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                  plan.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-ink-100 text-ink-500'
                }`}
              >
                {plan.isActive ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                {plan.isActive ? 'Live' : 'Hidden'}
              </span>
            </div>

            {plan.tagline && <p className="mt-1 text-sm text-ink-500">{plan.tagline}</p>}

            <div className="mt-3 flex items-baseline gap-1">
              {plan.paid ? (
                <>
                  <span className="text-2xl font-bold text-ink-900">${plan.priceUsd}</span>
                  <span className="text-sm text-ink-500">/mo · ₹{plan.priceInr}</span>
                </>
              ) : (
                <span className="text-2xl font-bold text-ink-900">Free</span>
              )}
            </div>

            <ul className="mt-3 space-y-1 text-sm text-ink-600">
              <li>{fmtQuota(plan.maxLinks)} links</li>
              <li>{fmtQuota(plan.maxProfilePages)} profile pages</li>
              <li>{fmtQuota(plan.maxLinksPerProfilePage)} links / page</li>
              <li>{fmtQuota(plan.maxWebhooks)} webhooks</li>
            </ul>

            {plan.paid && (
              <p className="mt-3 inline-flex items-center gap-1 text-[11px] text-ink-400">
                <CreditCard className="h-3 w-3" />
                Stripe {plan.stripePriceMonth ? '✓' : '—'} · Razorpay {plan.razorpayPlanMonth ? '✓' : '—'}
              </p>
            )}

            <div className="mt-4 flex gap-2 pt-3 border-t border-ink-100">
              <Button variant="outline" size="small" onClick={() => openEdit(plan)}>
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Button>
              {plan.key !== 'free' && (
                <Button variant="ghost" size="small" onClick={() => remove(plan)} className="text-rose-600 hover:bg-rose-50">
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>

      <Modal
        isOpen={Boolean(editing)}
        onClose={closeEditor}
        title={isNew ? 'Create plan' : `Edit ${editing?.label || ''}`}
        description={isNew ? 'Add a new pricing tier.' : `Plan key: ${editing?.key}`}
        size="lg"
      >
        {editing && (
          <PlanForm
            plan={editing}
            isNew={isNew}
            saving={saving}
            onField={setField}
            onSave={save}
            onCancel={closeEditor}
          />
        )}
      </Modal>
    </div>
  );
};

// --- Editor form ----------------------------------------------------------

const QuotaField = ({ label, value, onChange }) => {
  const unlimited = value === UNLIMITED;
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-ink-700">{label}</label>
      <div className="flex items-center gap-3">
        <input
          type="number"
          min="1"
          value={unlimited ? '' : value}
          disabled={unlimited}
          onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
          className="w-28 rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm text-ink-900 disabled:bg-ink-50 disabled:text-ink-400 focus:outline-none focus:ring-4 focus:ring-brand-100 focus:border-brand-500"
        />
        <label className="inline-flex items-center gap-1.5 text-sm text-ink-600">
          <input
            type="checkbox"
            checked={unlimited}
            onChange={(e) => onChange(e.target.checked ? UNLIMITED : 10)}
            className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
          />
          Unlimited
        </label>
      </div>
    </div>
  );
};

const Toggle = ({ label, checked, onChange, hint }) => (
  <label className="flex items-start gap-3 rounded-lg border border-ink-200 bg-ink-50 p-3 cursor-pointer">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-0.5 h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
    />
    <span>
      <span className="block text-sm font-medium text-ink-800">{label}</span>
      {hint && <span className="block text-xs text-ink-500">{hint}</span>}
    </span>
  </label>
);

const PlanForm = ({ plan, isNew, saving, onField, onSave, onCancel }) => (
  <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {isNew && (
        <Input
          label="Key"
          required
          value={plan.key}
          onChange={(e) => onField('key', e.target.value)}
          placeholder="team"
          hint="Lowercase id; immutable after creation."
        />
      )}
      <Input
        label="Label"
        required
        value={plan.label}
        onChange={(e) => onField('label', e.target.value)}
        placeholder="Team"
      />
      <Input
        label="Display order"
        type="number"
        value={plan.displayOrder}
        onChange={(e) => onField('displayOrder', e.target.value)}
        hint="Lower shows first."
      />
    </div>

    <Input
      label="Tagline"
      value={plan.tagline}
      onChange={(e) => onField('tagline', e.target.value)}
      placeholder="For growing teams"
    />

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Toggle
        label="Paid plan"
        checked={plan.paid}
        onChange={(v) => onField('paid', v)}
        hint="Off = free tier (no checkout)."
      />
      <Toggle
        label="Show on pricing page"
        checked={plan.isActive}
        onChange={(v) => onField('isActive', v)}
        hint="Hidden plans stay configured but aren't sold."
      />
      <Toggle
        label="Highlight as “Popular”"
        checked={plan.isHighlighted}
        onChange={(v) => onField('isHighlighted', v)}
      />
    </div>

    {plan.paid && (
      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Price (USD / mo)"
          type="number"
          value={plan.priceUsd}
          onChange={(e) => onField('priceUsd', e.target.value)}
        />
        <Input
          label="Price (INR / mo)"
          type="number"
          value={plan.priceInr}
          onChange={(e) => onField('priceInr', e.target.value)}
        />
      </div>
    )}

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <QuotaField label="Max links" value={plan.maxLinks} onChange={(v) => onField('maxLinks', v)} />
      <QuotaField label="Max profile pages" value={plan.maxProfilePages} onChange={(v) => onField('maxProfilePages', v)} />
      <QuotaField label="Max links / profile page" value={plan.maxLinksPerProfilePage} onChange={(v) => onField('maxLinksPerProfilePage', v)} />
      <QuotaField label="Max webhooks" value={plan.maxWebhooks} onChange={(v) => onField('maxWebhooks', v)} />
    </div>

    {plan.paid && (
      <div className="rounded-lg border border-ink-200 p-4 space-y-4">
        <p className="text-sm font-medium text-ink-700">Provider IDs</p>
        <p className="text-xs text-ink-500 -mt-3">
          Enter the Stripe Price ID / Razorpay Plan ID for each interval. A checkout button only
          appears when its ID is set and the provider is enabled on the server.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="Stripe price — monthly" value={plan.stripePriceMonth || ''} onChange={(e) => onField('stripePriceMonth', e.target.value)} placeholder="price_..." />
          <Input label="Stripe price — yearly" value={plan.stripePriceYear || ''} onChange={(e) => onField('stripePriceYear', e.target.value)} placeholder="price_..." />
          <Input label="Razorpay plan — monthly" value={plan.razorpayPlanMonth || ''} onChange={(e) => onField('razorpayPlanMonth', e.target.value)} placeholder="plan_..." />
          <Input label="Razorpay plan — yearly" value={plan.razorpayPlanYear || ''} onChange={(e) => onField('razorpayPlanYear', e.target.value)} placeholder="plan_..." />
        </div>
      </div>
    )}

    <div className="flex justify-end gap-3 pt-2 border-t border-ink-100">
      <Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
      <Button onClick={onSave} loading={saving} disabled={!plan.label || (isNew && !plan.key)}>
        {isNew ? 'Create plan' : 'Save changes'}
      </Button>
    </div>
  </div>
);

export default PlanManagement;
